import { useRef, useMemo, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import {
  IOrderData,
  ISalesOrder,
  ISaleOrderItem,
  IOrderDocument,
  ChoicesOrderStatus,
} from '@/types';
import { useRouter } from '@/routes/hooks';
import { paths } from '@/routes/paths';
import { useBoolean, useFetchData } from '@/hooks';
import { ShowIf } from '@/utils';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { IMilestoneSelected } from '@/components/data-display/billing-milestones/types';
import { Container } from '@mui/material';
import { BillingOrderNewEditFirstStepForm } from './billing-order-new-edit-firststep-form';
import { BillingOrderNewEditSecondStepForm } from './billing-order-new-edit-secondstep-form';
import { BillingOrderNewEditThirdStepForm } from './billing-order-new-edit-thirdstep-form';

type Props = {
  currentOrganization?: { _id: string; name?: string };
  currentExistsOrder?: any;
};

const transformData = (data: any[]) => {
  const result: Record<string, any> = {};

  data.forEach(
    ({
      legalEntity,
      salesOrdersItems,
      documents,
    }: {
      legalEntity: any;
      salesOrdersItems: any;
      documents: IOrderDocument[];
    }) => {
      if (!legalEntity?._id) return;

      if (!result[legalEntity._id]) {
        result[legalEntity._id] = {
          _id: legalEntity._id,
          label: legalEntity.legal_name,
          legal_name: legalEntity.legal_name,
          tax_number: legalEntity.tax_number || null,
          documents: documents || [],
          items: [],
        };
      }

      salesOrdersItems?.forEach(({ value, amount, orderItem, unitMeasure }: ISaleOrderItem) => {
        result[legalEntity._id].items.push({
          value,
          amount,
          itemId: orderItem,
          unitMeasure,
        });
      });
    }
  );

  return Object.values(result);
};

//TODO: Remove this component and their children after the QA Validations
export default function BillingOrderNewEditStepsForm({
  currentOrganization,
  currentExistsOrder,
}: Props) {
  const router = useRouter();
  const { t } = useTranslation();
  const idBackTo = currentOrganization?._id;
  const referenceOV = currentExistsOrder?.OVNumber;
  const changeStatus = useBoolean(false);
  const isSkipStatus = useBoolean(false);
  const [milestonesResetTrigger, setMilestonesResetTrigger] = useState(0);
  const previousMilestones = useRef<string[]>([]);

  const { statusCreated, validBill, initialActiveStep, initialMilestones } = useMemo(() => {
    const statusCreated =
      currentExistsOrder && currentExistsOrder.status !== ChoicesOrderStatus.created;

    const validBill = currentExistsOrder
      ? currentExistsOrder.status === ChoicesOrderStatus.completed ||
        currentExistsOrder.status === ChoicesOrderStatus.created
      : true;

    let initialActiveStep = 0;

    if (currentExistsOrder?.status === ChoicesOrderStatus.completed) {
      initialActiveStep = 2;
    } else if (
      currentExistsOrder?.salesOrders[0]?.legalEntity?.length > 0 ||
      currentExistsOrder?.salesOrders[0]?.OVNumber ||
      (statusCreated && !referenceOV) ||
      !validBill
    ) {
      initialActiveStep = 2;
    } else if (referenceOV && !currentExistsOrder?.salesOrders[0]?.OVNumber) {
      initialActiveStep = 1;
    } else if (currentExistsOrder?.status === ChoicesOrderStatus.created) {
      initialActiveStep = 0;
    }

    const initialMilestones = currentExistsOrder?.milestones
      ? currentExistsOrder.milestones
          .map((milestone: string | IMilestoneSelected) =>
            typeof milestone === 'string' ? milestone : milestone._id
          )
          .filter((milestoneId: string | undefined): milestoneId is string => Boolean(milestoneId))
      : [];

    return { statusCreated, validBill, initialActiveStep, initialMilestones };
  }, [currentExistsOrder, referenceOV]);

  const [activeStep, setActiveStep] = useState(initialActiveStep);

  useEffect(() => {
    if (previousMilestones.current.length === 0 && initialMilestones.length > 0) {
      previousMilestones.current = initialMilestones;
    }
  }, [initialMilestones]);

  const [orderData, setOrderData] = useState<IOrderData>(() =>
    currentExistsOrder ? { order: currentExistsOrder } : { order: null }
  );

  const steps = ['Milestones', 'Billing', 'Confirmation'];

  const { data: partnersData, isLoading: partnersIsLoading } = useFetchData(
    [`all-partners`],
    'partners/all'
  );
  const { data: organizationsData, isLoading: organizationsIsLoading } = useFetchData(
    [`all-organizations`],
    'organizations/all'
  );

  const handleNext = (data: Partial<IOrderData>) => {
    if (activeStep === 0) {
      data.salesOrders =
        (transformData(currentExistsOrder?.salesOrders ?? []) as unknown as ISalesOrder[]) || [];

      const currentMilestones = data.order?.milestones || [];
      const currentMilestonesIds = currentMilestones
        .map((m) => (typeof m === 'string' ? m : m._id))
        .filter((id): id is string => Boolean(id));

      if (!isEqual(previousMilestones.current, currentMilestonesIds)) {
        setMilestonesResetTrigger((prev) => prev + 1);
        previousMilestones.current = currentMilestonesIds;
        if (data.salesOrders) {
          delete data.salesOrders;
        }
      }
    }
    if (activeStep === 1 && !data.salesOrders) {
      isSkipStatus.onTrue();
    }
    if (data?.order?.changeValidator) {
      delete data.order.changeValidator;
      changeStatus.onTrue();
    }
    setOrderData((prevData) => ({
      ...prevData,
      ...data,
    }));
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    if (activeStep === 1) {
      changeStatus.onFalse();
      setOrderData((prevData) => {
        const newData = { ...prevData };
        delete newData.salesOrders;
        return newData;
      });
    }
    if (activeStep === 2) {
      isSkipStatus.onFalse();
    }

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setOrderData({});
    setActiveStep(0);
  };

  const handleCancel = () => {
    if (idBackTo) {
      router.push(paths.management.accounts.organizations.tab(`${idBackTo}`, 'billing'));
    } else {
      router.push(paths.management.accounts.billingOrders.root);
    }
  };

  const finalOrder = { ...currentExistsOrder, ...(orderData?.order || {}) };

  return (
    <Container maxWidth={false}>
      <Box marginTop={4} sx={{ width: '100%' }}>
        <ShowIf condition={!statusCreated}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Stepper activeStep={activeStep} sx={{ width: '80%' }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{t(label)}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </ShowIf>
        {!organizationsIsLoading && !partnersIsLoading && (
          <Box marginTop={4}>
            {activeStep === 0 && !statusCreated && (
              <BillingOrderNewEditFirstStepForm
                onNext={handleNext}
                onCancel={handleCancel}
                currentOrganization={currentOrganization}
                currentOrder={finalOrder}
                organizations={organizationsData?.data || organizationsData || []}
              />
            )}
            {activeStep === 1 && orderData && orderData.order && (
              <BillingOrderNewEditSecondStepForm
                onNext={handleNext}
                onBack={handleBack}
                onReset={handleReset}
                onCancel={handleCancel}
                currentOrganization={orderData.order.organization}
                currentOrder={finalOrder}
                currentSalesOrder={orderData?.salesOrders || []}
                milestonesResetTrigger={milestonesResetTrigger}
              />
            )}
            {activeStep === 2 && (
              <BillingOrderNewEditThirdStepForm
                currentOrder={!isSkipStatus.value ? orderData : { order: orderData?.order }}
                currentExistsOrder={currentExistsOrder}
                onCancel={handleCancel}
                onBack={handleBack}
                onReset={handleReset}
              />
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
}
